import { Link } from 'react-router';
import { useParapheur } from '@/api/hooks';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, Kpi, PageHeader, Vide } from '@/components/common';
import { InstructionsTable, PrioriteBadge, StatutFactureBadge } from '@/components/courrier';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, montant } from '@/lib/utils';

/** Parapheur unique du Directeur Général : courriers à annoter, factures à orienter, réponses à valider, réaffectations demandées. */
export function Parapheur() {
  const session = useSession();
  const u = session!.utilisateur;
  const { data, isLoading, error } = useParapheur();
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const c = data.compteurs;

  return (
    <div className="space-y-6">
      <PageHeader
        titre="Parapheur du Directeur Général"
        sousTitre="Un seul point d'entrée pour tout ce qui attend une décision : annotation, orientation, validation des réponses et réaffectations."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/instructions">Registre des instructions</Link>
            </Button>
            {(u.profils.includes('DIRECTEUR_GENERAL') || u.profils.includes('ADMINISTRATEUR')) && (
              <Button variant="outline" asChild>
                <Link to="/delegations">Délégations</Link>
              </Button>
            )}
          </>
        }
      />
      {data.parDelegation && (
        <Alert>
          <AlertTitle>Vous agissez par délégation du Directeur Général</AlertTitle>
          <AlertDescription>Chaque décision est tracée à votre nom, pour le compte du titulaire. Les courriers confidentiels ne vous sont pas présentés.</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Courriers à annoter" valeur={c.courriers} accent={c.courriers > 0 ? 'alerte' : undefined} />
        <Kpi label="Factures à orienter" valeur={c.factures} accent={c.factures > 0 ? 'alerte' : undefined} />
        <Kpi label="Réponses à valider" valeur={c.reponses} />
        <Kpi label="Réaffectations demandées" valeur={c.reaffectations} accent={c.reaffectations > 0 ? 'critique' : undefined} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Courriers à annoter ({data.courriers.length})</CardTitle>
          <CardDescription>Soumis par l'assistante, avec ou sans l'analyse du conseiller technique.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.courriers.length === 0 ? (
            <Vide message="Aucun courrier en attente." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead className="hidden sm:table-cell">Reçu le</TableHead>
                  <TableHead>Expéditeur</TableHead>
                  <TableHead>Objet</TableHead>
                  <TableHead className="hidden md:table-cell">Analyse</TableHead>
                  <TableHead>Priorité</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.courriers.map((x) => (
                  <TableRow key={x.id}>
                    <TableCell>
                      <Link to={`/courrier/${x.id}`} className="font-medium text-primary hover:underline">
                        {x.numero}
                      </Link>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{dateFr(x.dateReception)}</TableCell>
                    <TableCell className="max-w-[22ch] truncate">{x.expediteur}</TableCell>
                    <TableCell className="max-w-[36ch] truncate">
                      {x.confidentiel && <span className="mr-1 text-xs font-semibold text-red-700">Confidentiel ·</span>}
                      {x.objet}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{x.avecAnalyse ? `Avis de ${x.conseiller?.nom ?? 'CT'}` : <span className="text-muted-foreground">sans avis</span>}</TableCell>
                    <TableCell>
                      <PrioriteBadge priorite={x.priorite} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Factures à orienter ({data.factures.length})</CardTitle>
          <CardDescription>Orientation vers la Finance ou suspension motivée ; objectif de paiement sous 30 jours.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.factures.length === 0 ? (
            <Vide message="Aucune facture en attente." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Fournisseur</TableHead>
                  <TableHead className="hidden md:table-cell">Objet</TableHead>
                  <TableHead className="text-right">Montant</TableHead>
                  <TableHead className="hidden sm:table-cell">Reçue depuis</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.factures.map((x) => (
                  <TableRow key={x.id}>
                    <TableCell>
                      <Link to={`/factures/${x.id}`} className="font-medium text-primary hover:underline">
                        {x.numero}
                      </Link>
                      <div className="text-xs text-muted-foreground">{x.numeroFacture}</div>
                    </TableCell>
                    <TableCell className="max-w-[22ch] truncate">{x.fournisseur}</TableCell>
                    <TableCell className="hidden max-w-[32ch] truncate md:table-cell">{x.objet}</TableCell>
                    <TableCell className="num text-right">{montant(x.montantUsd, true)}</TableCell>
                    <TableCell className={cn('hidden sm:table-cell', x.delaiDepasse && 'font-semibold text-red-700', x.procheDelai && !x.delaiDepasse && 'text-amber-700')}>{x.jours} j</TableCell>
                    <TableCell>
                      <StatutFactureBadge statut={x.statut} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Réponses à valider ({data.reponses.length})</CardTitle>
          <CardDescription>Clôturer, demander un complément ou émettre une nouvelle instruction.</CardDescription>
        </CardHeader>
        <CardContent>
          <InstructionsTable instructions={data.reponses} vide="Aucune réponse en attente." />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Réaffectations demandées ({data.reaffectations.length})</CardTitle>
          <CardDescription>Le responsable estime que l'instruction ne relève pas de sa compétence.</CardDescription>
        </CardHeader>
        <CardContent>
          <InstructionsTable instructions={data.reaffectations} vide="Aucune demande de réaffectation." />
        </CardContent>
      </Card>
    </div>
  );
}
